import { useEffect } from "react";

const thumbnailFeatureLabelClassName = ".thumbnail-feature-label";
const innerThumbnailClassName = ".image-gallery-thumbnail-inner";

/**
 * Custom hook that adds feature labels to image gallery thumbnails
 * @param galleryVisible - Whether the gallery is currently visible
 * @param featureLabels - Array of labels to display on each thumbnail
 */
export function useThumbnailLabels(
  galleryVisible: boolean,
  featureLabels: string[],
): void {
  useEffect(() => {
    if (!galleryVisible || !featureLabels.length) {
      return;
    }

    // Wait for gallery to render, then add labels
    const addLabels = () => {
      const thumbnails = document.querySelectorAll(innerThumbnailClassName);
      thumbnails.forEach((thumbnail, index) => {
        // Skip if label already exists
        if (thumbnail.querySelector(thumbnailFeatureLabelClassName)) {
          return;
        }

        const label = featureLabels[index];
        if (label) {
          createThumbnailFeatureLabel(thumbnail, label);
        }
      });
    };

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(addLabels, 200);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [galleryVisible, featureLabels]);
}

/**
 * Creates and appends a feature label to a thumbnail element
 * @param thumbnail - The thumbnail element to add the label to
 * @param label - The text content for the label
 */
export const createThumbnailFeatureLabel = (
  thumbnail: Element,
  label: string,
): void => {
  const thumbnailEl = thumbnail as HTMLElement;
  const labelDiv = document.createElement("div");
  labelDiv.className = "thumbnail-feature-label";
  labelDiv.textContent = label;
  labelDiv.style.cssText = `
    position: absolute;
    top: 4px;
    left: 4px;
    background-color: rgba(128, 128, 128, 0.85);
    color: white;
    padding: 4px 6px;
    border-radius: 2px;
    font-size: 10px;
    font-weight: 500;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    pointer-events: none;
    z-index: 10;
  `;
  thumbnailEl.style.position = "relative";
  thumbnailEl.appendChild(labelDiv);
};
